import { useEffect, useState } from "react";
import { Download, Share, Plus, X } from "lucide-react";
import { Button } from "./ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "./ui/dialog";

const isStandalone = () =>
  window.matchMedia("(display-mode: standalone)").matches || window.navigator.standalone === true;
const isIOS = () => /iphone|ipad|ipod/i.test(window.navigator.userAgent);

export function InstallPWA({ variant = "sidebar" }) {
  const [deferred, setDeferred] = useState(null);
  const [showHelp, setShowHelp] = useState(false);
  const [installed, setInstalled] = useState(isStandalone());

  useEffect(() => {
    const onPrompt = (e) => { e.preventDefault(); setDeferred(e); };
    const onInstalled = () => { setInstalled(true); setDeferred(null); };
    window.addEventListener("beforeinstallprompt", onPrompt);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onPrompt);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  if (installed) return null;

  const handleClick = async () => {
    if (deferred) {
      deferred.prompt();
      const { outcome } = await deferred.userChoice;
      if (outcome === "accepted") setInstalled(true);
      setDeferred(null);
    } else {
      setShowHelp(true);
    }
  };

  const btnClass = variant === "sidebar"
    ? "flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm text-brand hover:bg-brand-light w-full transition-colors"
    : "text-ink-muted";

  return (
    <>
      {variant === "sidebar" ? (
        <button onClick={handleClick} data-testid="install-pwa-btn" className={btnClass}>
          <Download className="w-4 h-4" strokeWidth={1.5} /> App installeren
        </button>
      ) : (
        <button onClick={handleClick} data-testid="install-pwa-btn-mobile" className="p-1">
          <Download className="w-5 h-5 text-brand" strokeWidth={1.5} />
        </button>
      )}

      <Dialog open={showHelp} onOpenChange={setShowHelp}>
        <DialogContent className="rounded-2xl max-w-sm" data-testid="install-help-dialog" aria-describedby={undefined}>
          <DialogHeader>
            <DialogTitle className="font-display flex items-center gap-2">
              <Download className="w-5 h-5 text-brand" /> App op je beginscherm
            </DialogTitle>
          </DialogHeader>
          {isIOS() ? (
            <div className="space-y-4 text-sm text-ink">
              <p className="text-ink-muted">Op je iPhone of iPad zet je de app zo op je beginscherm:</p>
              <Step n="1" icon={<Share className="w-4 h-4 text-brand" />}>
                Tik onderin op het <strong>deel-icoon</strong> (vierkant met pijltje omhoog) in Safari.
              </Step>
              <Step n="2" icon={<Plus className="w-4 h-4 text-brand" />}>
                Kies <strong>“Zet op beginscherm”</strong>.
              </Step>
              <Step n="3" icon={<Download className="w-4 h-4 text-brand" />}>
                Tik op <strong>“Voeg toe”</strong>. Het Nelleke Holt-icoon verschijnt op je beginscherm.
              </Step>
            </div>
          ) : (
            <div className="space-y-4 text-sm text-ink">
              <p className="text-ink-muted">Zet de app zo op je beginscherm:</p>
              <Step n="1" icon={<span className="text-brand font-bold">⋮</span>}>
                Open het <strong>menu</strong> (drie puntjes) rechtsboven in je browser.
              </Step>
              <Step n="2" icon={<Download className="w-4 h-4 text-brand" />}>
                Kies <strong>“App installeren”</strong> of <strong>“Toevoegen aan startscherm”</strong>.
              </Step>
              <Step n="3" icon={<Plus className="w-4 h-4 text-brand" />}>
                Bevestig. De app opent voortaan als een eigen app.
              </Step>
            </div>
          )}
          <Button onClick={() => setShowHelp(false)} className="rounded-full bg-brand hover:bg-brand-hover text-white mt-2">
            Begrepen
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
}

const Step = ({ n, icon, children }) => (
  <div className="flex items-start gap-3">
    <div className="w-7 h-7 rounded-full bg-brand-light flex items-center justify-center shrink-0 text-brand text-sm font-medium">{n}</div>
    <div className="flex-1 flex items-start gap-2">
      <span className="mt-0.5">{icon}</span>
      <span>{children}</span>
    </div>
  </div>
);
